import { tinykeys } from "tinykeys";
import type { Actions } from "./actions";
import { useBoardUI } from "./store";

const editingText = () => {
  const el = document.activeElement as HTMLElement | null;
  return (
    !!el &&
    (el.isContentEditable || el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT")
  );
};

export function bindShortcuts(
  actions: Actions,
  extra: {
    zoomFit: () => void;
    zoomIn: () => void;
    zoomOut: () => void;
    resetZoom: () => void;
    setSpace: (v: boolean) => void;
  },
) {
  const guard = (fn: (e: KeyboardEvent) => void) => (e: KeyboardEvent) => {
    if (editingText()) return;
    e.preventDefault();
    fn(e);
  };
  const unsub = tinykeys(window, {
    "$mod+z": guard(() => actions.undo()),
    "$mod+Shift+z": guard(() => actions.redo()),
    "$mod+y": guard(() => actions.redo()),
    "$mod+a": guard(() => actions.selectAll()),
    "$mod+d": guard(() => actions.duplicate()),
    "$mod+k": guard(() => actions.connect()),
    Delete: guard(() => actions.delete()),
    Backspace: guard(() => actions.delete()),
    Escape: () => {
      const ui = useBoardUI.getState();
      if (ui.editingId) (document.activeElement as HTMLElement | null)?.blur();
      ui.clearSelection();
      ui.setContextMenu(null);
    },
    ArrowLeft: guard(() => actions.nudge(-10, 0)),
    ArrowRight: guard(() => actions.nudge(10, 0)),
    ArrowUp: guard(() => actions.nudge(0, -10)),
    ArrowDown: guard(() => actions.nudge(0, 10)),
    "Shift+ArrowLeft": guard(() => actions.nudge(-50, 0)),
    "Shift+ArrowRight": guard(() => actions.nudge(50, 0)),
    "Shift+ArrowUp": guard(() => actions.nudge(0, -50)),
    "Shift+ArrowDown": guard(() => actions.nudge(0, 50)),
    "$mod+Shift+ArrowUp": guard(() => actions.front()),
    "$mod+Shift+ArrowDown": guard(() => actions.back()),
    z: guard(() => extra.zoomFit()),
    p: guard(() => window.dispatchEvent(new CustomEvent("mn:present"))),
    "Shift+?": guard(() => window.dispatchEvent(new CustomEvent("mn:help"))),
    "$mod+0": guard(() => extra.resetZoom()),
    "$mod+Equal": guard(() => extra.zoomIn()),
    "$mod+Minus": guard(() => extra.zoomOut()),
    Enter: guard(() => {
      const ui = useBoardUI.getState();
      const [id] = [...ui.selection];
      if (!id || ui.selection.size !== 1) return;
      const type = actions.cards.get(id)?.get("type");
      if (type === "board" || type === "alias") actions.openTile(id);
      else ui.setEditing(id);
    }),
  });
  const down = (e: KeyboardEvent) => {
    if (e.code === "Space" && !editingText()) {
      e.preventDefault();
      extra.setSpace(true);
    }
  };
  const up = (e: KeyboardEvent) => e.code === "Space" && extra.setSpace(false);
  window.addEventListener("keydown", down);
  window.addEventListener("keyup", up);
  return () => {
    unsub();
    window.removeEventListener("keydown", down);
    window.removeEventListener("keyup", up);
  };
}
